import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import Carousel from 'LuxKit/Carousel/Carousel'
import {
  getElementLogicalInlineStartOffset,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import type { SvgIconComponent } from 'LuxKit/SvgIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useDetectCarouselSlide from 'hooks/useDetectCarouselSlide'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperiencesHomepageVerifiedExperiencesCount } from 'lib/experiences/experiencesHomepageUtils'

const METRIC_ITEMS = [
  { key: 'verified-experiences', Icon: LineSightseeingIcon },
  { key: 'secured-bookings', Icon: LineCheckCircleIcon },
  { key: 'customer-rating', Icon: LineStarIcon },
] as const satisfies ReadonlyArray<{
  key: string
  Icon: SvgIconComponent
}>

type MetricKey = (typeof METRIC_ITEMS)[number]['key']

const MetricsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(28)};

  ${mediaQueryUp.tablet} {
    justify-content: center;
    gap: ${rem(40)};
  }
`

const MetricsCarousel = styled(Carousel)`
  flex: 1;
  min-inline-size: 0;

  > div > div {
    padding-inline: 0;
    margin-inline: 0;
    scroll-padding-inline: 0;
    max-width: none;
  }

  ${mediaQueryUp.tablet} {
    flex: 0 1 auto;

    > div > div {
      column-gap: ${rem(8)};
    }
  }
`

const MetricSlide = styled.div`
  min-inline-size: 0;
`

const MetricsPane = styled(Pane)`
  margin-bottom: -${rem(12)};

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

const MetricsPaneContent = styled.div`
  padding-block: ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(16)};
  }
`

function ExperiencesHomepageMetrics() {
  const intl = useIntl()
  const isTabletUp = useScreenSizeUp('tablet')
  const metrics = useAppSelector(state => state.experience.experienceMetrics)
  const fetching = useAppSelector(state => state.experience.fetchingExperienceMetrics)
  const error = useAppSelector(state => state.experience.experienceMetricsError)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const slideElements = useRef<Array<HTMLDivElement | null>>([])
  const [activeIndex, setActiveIndex] = useState(0)

  const onScroll = useDetectCarouselSlide(
    carouselRef,
    slideElements,
    setActiveIndex,
    'start',
  )

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index)
    const carousel = carouselRef.current
    const slideElement = slideElements.current[index]
    if (carousel && slideElement) {
      scrollToLogicalPosition(carousel, {
        left: getElementLogicalInlineStartOffset(slideElement, carousel),
        behavior: 'smooth',
      })
    }
  }, [])

  const labelsByKey = useMemo((): Record<MetricKey, string> | undefined => {
    if (!metrics) return undefined

    return {
      'verified-experiences': intl.formatMessage(
        {
          id: 'XpAsCmyGdpYFsPJnzecL',
          defaultMessage: '{count, plural, one {#+ verified experience} other {#+ verified experiences}}',
          description: 'Experiences homepage metrics bar label showing the current global verified experience count',
        },
        { count: getExperiencesHomepageVerifiedExperiencesCount(metrics.verifiedExperiencesCount) },
      ),
      'secured-bookings': intl.formatMessage(
        {
          id: 'TGJ7IaN_ZGXwnghUjtlo',
          defaultMessage: '{count, plural, one {# secured booking this week} other {# secured bookings this week}}',
          description: 'Experiences homepage metrics bar label showing the total experience bookings created this week',
        },
        { count: metrics.securedBookingsThisWeek },
      ),
      'customer-rating': intl.formatMessage(
        {
          id: 'BSf7mlFOsBHf7Ao4xedU',
          defaultMessage: 'Average customer rating of {rating}/10',
          description: 'Experiences homepage metrics bar label showing the weighted average customer provider rating',
        },
        {
          rating: intl.formatNumber(metrics.averageCustomerRating, {
            maximumFractionDigits: 1,
          }),
        },
      ),
    }
  }, [intl, metrics])

  if (fetching || error || !labelsByKey) return null

  return (
    <MetricsPane background="light-background" paletteType="default">
      <MetricsPaneContent>
        <LayoutContainer>
          <MetricsRow>
            <MetricsCarousel
              ref={carouselRef}
              onScroll={onScroll}
              width="100%"
              tabletWidth="min-content"
              desktopWidth="min-content"
              alignment={isTabletUp ? 'center' : undefined}
              gutterSize={0}
              gutterStyle="default"
              snap="start"
              gap={28}
            >
              {METRIC_ITEMS.map(({ key, Icon }, index) => (
                <MetricSlide
                  key={key}
                  ref={(element) => {
                    slideElements.current[index] = element
                  }}
                >
                  <BodyTextBlock
                    variant="medium"
                    colour="light-foreground-alt"
                    wrap="no-wrap"
                    startIcon={<Icon size="M" colour="light-foreground-alt" />}
                  >
                    {labelsByKey[key]}
                  </BodyTextBlock>
                </MetricSlide>
              ))}
            </MetricsCarousel>
            <CSSBreakpoint max="mobile">
              <PaginationDots
                count={METRIC_ITEMS.length}
                activeIndex={activeIndex}
                slideDuration={0}
                onSelect={goToSlide}
              />
            </CSSBreakpoint>
          </MetricsRow>
        </LayoutContainer>
      </MetricsPaneContent>
    </MetricsPane>
  )
}

export default ExperiencesHomepageMetrics
